import { useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import {
  useWasteSummary,
  useWasteTrend,
  useWasteByPart,
  useWasteByMaterial,
} from "@/hooks/use-waste";
import { useProductionProducts } from "@/hooks/use-production";
import { formatDate, daysAgo, today } from "@/lib/utils";
import { TrendingDown, TrendingUp, Scale } from "lucide-react";
import { SkeletonCard } from "@/components/ui/Skeleton";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";

type Bucket = "daily" | "weekly" | "monthly";

/** Maps waste % to a green→amber→red colour scale */
function wasteColor(pct: number): string {
  if (pct < 1) return "#22c55e";   // green-500
  if (pct < 2) return "#84cc16";   // lime-500
  if (pct < 3) return "#eab308";   // yellow-500
  if (pct < 5) return "#f97316";   // orange-500
  return "#ef4444";                 // red-500
}

export function WasteAnalysis() {
  useSetPageHeader({
    title: "Yield & Waste",
    subtitle: "Material waste tracking, yield trends, and variance breakdown",
    icon: TrendingDown,
  });

  const [from, setFrom] = useState(() => daysAgo(90));
  const [to, setTo] = useState(() => today());
  const [product, setProduct] = useState<string | undefined>();
  const [bucket, setBucket] = useState<Bucket>("weekly");

  const { data: partsData } = useProductionProducts();
  const { data: summary, isLoading: loadingSummary } = useWasteSummary(from, to, product);
  const { data: trend } = useWasteTrend(from, to, product, bucket);
  const { data: byPart } = useWasteByPart(from, to);
  const { data: byMaterial } = useWasteByMaterial(from, to, product);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
          <input type="date" className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
          <input type="date" className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Product</label>
          <select className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={product || ""} onChange={(e) => setProduct(e.target.value || undefined)}>
            <option value="">All products</option>
            {(partsData || []).map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Bucket</label>
          <div className="flex rounded-md border border-input">
            {(["daily", "weekly", "monthly"] as Bucket[]).map((b) => (
              <button key={b} onClick={() => setBucket(b)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                  bucket === b
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-muted-foreground hover:bg-muted"
                }`}>
                {b.charAt(0).toUpperCase() + b.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {loadingSummary ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Scale className="h-3 w-3" /> Total Scrap
            </div>
            <p className="text-2xl font-bold">{summary.total_scrap_kg.toLocaleString()} kg</p>
            <p className="text-[10px] text-muted-foreground">{summary.total_runs.toLocaleString()} runs</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">Scrap Rate</p>
            <p className="text-2xl font-bold">{summary.scrap_rate_pct}%</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3" /> Good Parts
            </div>
            <p className="text-2xl font-bold">{summary.total_good_parts.toLocaleString()}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <TrendingDown className="h-3 w-3" /> Rejected
            </div>
            <p className="text-2xl font-bold">{summary.total_rejected_parts.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Waste Trend Chart */}
      {trend && trend.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Waste % Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' })} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                contentStyle={{ backgroundColor: "var(--card))", border: "1px solid var(--border))" }}
                formatter={(value: number, name: string) => [
                  name === "scrap_rate_pct" ? `${value}%` : `${value.toLocaleString()} kg`,
                  name === "scrap_rate_pct" ? "Scrap Rate %" : "Total Scrap",
                ]}
                labelFormatter={(v) => formatDate(v)}
              />
              <Area type="monotone" dataKey="scrap_rate_pct" stroke="#f97316"
                fill="#f97316" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Two-column: By Part + By Material */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* By Part Bar Chart */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Waste % by Part</h3>
          {byPart && byPart.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(250, byPart.slice(0, 15).length * 32)}>
              <BarChart data={byPart.slice(0, 15)} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="part_name" tick={{ fontSize: 10 }} width={95} />
                <Tooltip
                  contentStyle={{ backgroundColor: "var(--card))", border: "1px solid var(--border))" }}
                  formatter={(value: number, name: string) => [
                    name === "scrap_rate_pct" ? `${value}%` : `${value.toLocaleString()} kg`,
                    name === "scrap_rate_pct" ? "Scrap Rate %" : "Total Scrap",
                  ]}
                />
                <Bar dataKey="scrap_rate_pct" radius={[0, 4, 4, 0]}>
                  {byPart.slice(0, 15).map((entry: { scrap_rate_pct: number }, index: number) => (
                    <Cell key={index} fill={wasteColor(entry.scrap_rate_pct)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No data</p>
          )}
        </div>

        {/* Top Waste Materials Table */}
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Top Waste Materials</h3>
          {byMaterial && byMaterial.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-2 py-2 font-medium">Material</th>
                    <th className="px-2 py-2 text-right font-medium">Runs</th>
                    <th className="px-2 py-2 text-right font-medium">Avg Var %</th>
                    <th className="px-2 py-2 text-right font-medium">Scrap %</th>
                    <th className="px-2 py-2 text-right font-medium">Total kg</th>
                  </tr>
                </thead>
                <tbody>
                  {byMaterial.map((m) => (
                    <tr key={m.material} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-2 py-2 max-w-[160px] truncate" title={m.material}>
                        {m.material}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                        {m.run_count.toLocaleString()}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">{m.avg_variance_pct}%</td>
                      <td className="px-2 py-2 text-right tabular-nums">{m.scrap_rate_pct}%</td>
                      <td className="px-2 py-2 text-right tabular-nums font-medium">
                        {m.total_scrap_kg.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">No data</p>
          )}
        </div>
      </div>
    </div>
  );
}
